import type { ReactNode } from "react";
import type {
	ButtonProps as AriaButtonProps,
	TooltipProps as AriaTooltipProps,
	TooltipTriggerComponentProps as AriaTooltipTriggerComponentProps,
	ButtonRenderProps,
} from "react-aria-components";

import {
	Button as AriaButton,
	OverlayArrow as AriaOverlayArrow,
	Tooltip as AriaTooltip,
	TooltipTrigger as AriaTooltipTrigger,
} from "react-aria-components";

import { cx } from "@/utils/cx";

interface TooltipProps
	extends AriaTooltipTriggerComponentProps, Omit<AriaTooltipProps, "children"> {
	/**
	 * Whether to show the arrow on the tooltip.
	 *
	 * @default false
	 */
	arrow?: boolean;
	/**
	 * Delay in milliseconds before the tooltip is shown.
	 *
	 * @default 300
	 */
	delay?: number;
	/**
	 * The description of the tooltip.
	 */
	description?: ReactNode;
	/**
	 * The title of the tooltip.
	 */
	title: ReactNode;
}

export const Tooltip = ({
	arrow = false,
	children,
	closeDelay = 0,
	crossOffset,
	defaultOpen,
	delay = 300,
	description,
	isDisabled,
	isOpen,
	offset = 6,
	onOpenChange,
	placement = "top",
	title,
	trigger,
	...tooltipProps
}: TooltipProps) => {
	const isTopOrBottomLeft = [
		"bottom end",
		"bottom left",
		"top end",
		"top left",
	].includes(placement);
	const isTopOrBottomRight = [
		"bottom right",
		"bottom start",
		"top right",
		"top start",
	].includes(placement);
	// Set negative cross offset for left and right placement to visually balance the tooltip.
	const calculatedCrossOffset = isTopOrBottomLeft
		? -12
		: isTopOrBottomRight
			? 12
			: 0;

	return (
		<AriaTooltipTrigger
			{...{
				closeDelay,
				defaultOpen,
				delay,
				isDisabled,
				isOpen,
				onOpenChange,
				trigger,
			}}
		>
			{children}

			<AriaTooltip
				{...tooltipProps}
				className={({ isEntering, isExiting }) =>
					cx(
						isEntering && "ease-out animate-in",
						isExiting && "ease-in animate-out",
					)
				}
				crossOffset={crossOffset ?? calculatedCrossOffset}
				offset={offset}
				placement={placement}
			>
				{({ isEntering, isExiting }) => (
					<div
						className={cx(
							"z-50 flex max-w-xs origin-(--trigger-anchor-point) flex-col items-start gap-1 rounded-lg bg-primary-solid px-3 shadow-lg will-change-transform",
							description ? "py-3" : "py-2",

							isEntering &&
								"ease-out animate-in fade-in zoom-in-95 in-placement-left:slide-in-from-right-0.5 in-placement-right:slide-in-from-left-0.5 in-placement-top:slide-in-from-bottom-0.5 in-placement-bottom:slide-in-from-top-0.5",
							isExiting &&
								"ease-in animate-out fade-out zoom-out-95 in-placement-left:slide-out-to-right-0.5 in-placement-right:slide-out-to-left-0.5 in-placement-top:slide-out-to-bottom-0.5 in-placement-bottom:slide-out-to-top-0.5",
						)}
					>
						<span className="text-xs font-semibold text-white">{title}</span>

						{description && (
							<span className="text-xs font-medium text-tooltip-supporting-text">
								{description}
							</span>
						)}

						{arrow && (
							<AriaOverlayArrow>
								<svg
									className="size-2.5 fill-bg-primary-solid in-placement-left:-rotate-90 in-placement-right:rotate-90 in-placement-top:rotate-0 in-placement-bottom:rotate-180"
									viewBox="0 0 100 100"
								>
									<path d="M0,0 L35.858,35.858 Q50,50 64.142,35.858 L100,0 Z" />
								</svg>
							</AriaOverlayArrow>
						)}
					</div>
				)}
			</AriaTooltip>
		</AriaTooltipTrigger>
	);
};

interface TooltipTriggerProps extends AriaButtonProps {
	className?: ((values: ButtonRenderProps) => string) | string;
}

export const TooltipTrigger = ({
	children,
	className,
	slot = null,
	...buttonProps
}: TooltipTriggerProps) => {
	return (
		<AriaButton
			{...buttonProps}
			className={(values) =>
				cx(
					"h-max w-max outline-hidden",
					typeof className === "function" ? className(values) : className,
				)
			}
			// Opt out of parent ButtonContext (e.g. Select/ComboBox trigger).
			// Without this, a label help button steals the field's trigger slot
			// and can break layout when the field mounts/unmounts.
			slot={slot}
		>
			{children}
		</AriaButton>
	);
};
